import { useState } from 'react';
import {
  setTovQueryData,
  useTovMutation,
  useTovQuery,
  useTovQueryClient,
} from '@tov-plus/frontend-data';
import {
  TovButton,
  SelectOption,
  TovFormSelectField,
  TovFormTextField,
  TovFormTextareaField,
  useToast,
  useTovForm,
  msg,
  TovCard,
} from '@tov-plus/ui';
import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import { useGuestAccess } from '../contexts/GuestAccessContext';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';

const taskBoardErrorMessage = msg(
  'pages.shared_planning_page.task_board_load_error',
);

type SharedPlanningView = Awaited<
  ReturnType<typeof guestApi.listSharedPlanning>
>;
type SharedPlanningItemEntry = SharedPlanningView[number]['items'][number];

type SharedPlanningClaimFormValues = {
  claimedQuantity: string;
  claimStatus: 'committed' | 'fulfilled' | 'cancelled';
  guestNote: string;
};

function parseClaimedQuantity(value: string) {
  const normalizedValue = value.trim();
  if (normalizedValue.length === 0) {
    return null;
  }

  const parsed = Number(normalizedValue);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
}

export function SharedPlanningPage() {
  const access = useGuestAccess();
  const notifications = useToast();
  const queryClient = useTovQueryClient();
  const sharedPlanningQueryKey =
    access.status === 'ready'
      ? guestQueryKeys.sharedPlanning(access.occasionSlug, access.guestToken)
      : guestQueryKeys.sharedPlanning('unknown-occasion', 'unknown-token');
  const sharedPlanningQuery = useTovQuery({
    queryKey: sharedPlanningQueryKey,
    enabled: access.status === 'ready',
    queryFn: () => {
      if (access.status !== 'ready') {
        throw new Error(taskBoardErrorMessage);
      }
      return guestApi.listSharedPlanning(
        access.occasionSlug,
        access.guestToken,
      );
    },
  });
  const [submittingItemId, setSubmittingItemId] = useState<string | null>(null);
  const claimMutation = useTovMutation({
    mutationFn: ({
      itemId,
      values,
    }: {
      itemId: string;
      values: SharedPlanningClaimFormValues;
    }) => {
      if (access.status !== 'ready') {
        throw new Error(taskBoardErrorMessage);
      }
      return guestApi.claimSharedPlanningItem(
        access.occasionSlug,
        access.guestToken,
        itemId,
        {
          claimedQuantity: parseClaimedQuantity(values.claimedQuantity),
          claimStatus: values.claimStatus,
          guestNote: values.guestNote.trim() || null,
        },
      );
    },
    onSuccess(next) {
      setTovQueryData(queryClient, sharedPlanningQueryKey, next);
      notifications.success(
        msg('pages.shared_planning_page.your_contribution_was_saved'),
      );
    },
    onError(error) {
      notifications.error(getGuestErrorMessage(error, taskBoardErrorMessage));
    },
    onSettled() {
      setSubmittingItemId(null);
    },
    invalidate: [sharedPlanningQueryKey],
  });
  if (access.status !== 'ready') {
    return null;
  }
  if (sharedPlanningQuery.isLoading) {
    return <StatePanel preset="loading" />;
  }
  if (sharedPlanningQuery.isError) {
    return (
      <StatePanel
        preset="unknown-error"
        detail={getGuestErrorMessage(
          sharedPlanningQuery.error,
          taskBoardErrorMessage,
        )}
        actions={
          <TovButton onClick={() => void sharedPlanningQuery.refetch()}>
            {msg('general.actions.retry')}
          </TovButton>
        }
      />
    );
  }
  const boards = sharedPlanningQuery.data ?? [];
  if (boards.length === 0) {
    return <StatePanel preset="empty" />;
  }
  return (
    <div className="page-grid">
      <TovCard variant="content" isWide className="hero-card">
        <p className="page-eyebrow">
          {msg('general.modules.task_boards.name')}
        </p>
        <h2>
          {msg(
            'pages.shared_planning_page.pages_shared_planning_page_coordinate_what_you_can_bring_or_help_with',
          )}
        </h2>
        <p>
          {msg(
            'pages.shared_planning_page.pages_shared_planning_page_these_boards_are_the_structured_collaboration_layer_for',
          )}{' '}
        </p>
      </TovCard>

      {boards.map((board) => (
        <TovCard variant="content" isWide key={board.board.id}>
          <p className="page-eyebrow">
            {board.event?.displayName ??
              msg(
                'pages.shared_planning_page.pages_shared_planning_page_occasion_wide_board',
              )}
          </p>
          <h2>{board.board.title}</h2>
          <p>
            {board.board.description ??
              msg(
                'pages.shared_planning_page.pages_shared_planning_page_no_extra_board_notes_yet',
              )}
          </p>
          <div className="detail-list detail-list--stacked">
            {board.items.map((entry) => (
              <article key={entry.item.id}>
                <strong>
                  {entry.item.title} · {entry.item.status}
                </strong>
                <span>
                  {entry.item.description ??
                    msg(
                      'pages.shared_planning_page.pages_shared_planning_page_no_extra_details_provided',
                    )}
                </span>
                <span>
                  {entry.progress.activeClaimCount}{' '}
                  {msg(
                    'pages.shared_planning_page.pages_shared_planning_page_claim_s',
                  )}{' '}
                  {entry.progress.quantityNeeded !== null
                    ? ` · ${entry.progress.quantityClaimed ?? 0}/${entry.progress.quantityNeeded} ${entry.item.quantityUnit ?? ''}`
                    : ''}
                </span>
                <SharedPlanningClaimForm
                  entry={entry}
                  submitting={submittingItemId === entry.item.id}
                  onSubmit={(values) => {
                    setSubmittingItemId(entry.item.id);
                    claimMutation.mutate({ itemId: entry.item.id, values });
                  }}
                />
              </article>
            ))}
          </div>
        </TovCard>
      ))}
    </div>
  );
}

function SharedPlanningClaimForm({
  entry,
  submitting,
  onSubmit,
}: {
  entry: SharedPlanningItemEntry;
  submitting: boolean;
  onSubmit(values: SharedPlanningClaimFormValues): void;
}) {
  const form = useTovForm({
    defaultValues: {
      claimedQuantity:
        entry.myClaim?.claimedQuantity === null ||
        entry.myClaim?.claimedQuantity === undefined
          ? ''
          : String(entry.myClaim.claimedQuantity),
      claimStatus: entry.myClaim?.claimStatus ?? 'committed',
      guestNote: entry.myClaim?.guestNote ?? '',
    } satisfies SharedPlanningClaimFormValues,
    onSubmit: ({ value }) => onSubmit(value),
  });

  return (
    <form
      className="detail-list detail-list--stacked"
      onSubmit={async (event) => {
        event.preventDefault();
        await form.handleSubmit();
      }}
    >
      <TovFormTextField
        form={form}
        label={msg('general.fields.quantity.label')}
        name="claimedQuantity"
        placeholder={
          entry.item.quantityNeeded !== null
            ? String(entry.item.quantityNeeded)
            : '1'
        }
      />
      <TovFormSelectField
        form={form}
        label={msg('general.fields.status.label')}
        name="claimStatus"
      >
        <SelectOption value="committed">
          {msg(
            'pages.shared_planning_page.pages_shared_planning_page_committed',
          )}
        </SelectOption>
        <SelectOption value="fulfilled">
          {msg('general.status.fulfilled')}
        </SelectOption>
        <SelectOption value="cancelled">
          {msg(
            'pages.shared_planning_page.pages_shared_planning_page_cancel_my_claim',
          )}{' '}
        </SelectOption>
      </TovFormSelectField>
      <TovFormTextareaField
        form={form}
        label={msg(
          'pages.shared_planning_page.pages_shared_planning_page_note',
        )}
        name="guestNote"
      />
      <TovButton disabled={submitting} type="submit">
        {entry.myClaim
          ? msg(
              'pages.shared_planning_page.pages_shared_planning_page_update_contribution',
            )
          : msg(
              'pages.shared_planning_page.pages_shared_planning_page_claim_item',
            )}
      </TovButton>
    </form>
  );
}
